import { CommonModule } from '@angular/common';
import { Component, effect, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DatabaseManagement } from '@app/models/admin.model';
import { BaseMasterDataResponse } from '@app/models/shared.models';
import { MasterdataService } from '@app/services/admin/masterdata.service';
import { GenericDataService } from '@app/services/generic-data.service';
import { GenericStore } from '@app/stores/generic.store';
import { AddActionButtonComponent } from '@app/ui-components/add-action-button/add-action-button.component';
import { GridTableComponent } from '@app/ui-components/grid-table/grid-table.component';
import { LinkCellRendererComponent } from '@app/ui-components/grid-table/link-cell-renderer/link-cell-renderer.component';
import { SwitchCellRendererComponent } from '@app/ui-components/grid-table/switch-cell-renderer/switch-cell-renderer.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ColDef, ValueGetterParams } from 'ag-grid-community';

@Component({
  selector: 'app-project-classification-list',
  imports: [
    CommonModule,
    GridTableComponent,
    TranslatePipe,
    AddActionButtonComponent,
  ],
  templateUrl: './project-classification-list.component.html',
  styleUrl: './project-classification-list.component.scss',
})
export class ProjectClassificationListComponent implements OnInit {
  database: DatabaseManagement[] = [];
  columns: ColDef[] = [];

  private store = inject(GenericStore);
  private databaseSelector: string = 'projectClassification';

  constructor(
    private service: GenericDataService,
    private translate: TranslateService,
    private masterdataService: MasterdataService,
    private router: Router
  ) {
    this.translate.onLangChange.subscribe(() =>
      this.buildColumns(this.store.languageEntities())
    );

    effect(() => {
      const languages = this.store.languageEntities();
      this.buildColumns(languages);
    });
  }

  private buildColumns(languages: BaseMasterDataResponse[]): void {
    this.columns = [
      {
        field: 'isActive',
        headerName: '',
        width: 100,
        editable: true,
        sort: 'desc',
        cellRenderer: SwitchCellRendererComponent,
        cellRendererParams: {
          fieldKey: 'isActive',
          onToggle: (row: DatabaseManagement) => this.onActiveToggle(row),
        },
      },
      {
        field: 'code',
        headerName: this.translate.instant('admin.common.code'),
        cellRenderer: LinkCellRendererComponent,
        cellRendererParams: {
          context: {
            baseRoute: `/admin/database/${this.databaseSelector}/`,
          },
        },
      },
      ...this.store.languageEntities().map(
        (entity) =>
          ({
            field: entity.description,
            valueGetter: (params: ValueGetterParams<any, string>) =>
              params.data.localized[entity.description] || '-',
            headerName: entity.code,
          } as ColDef)
      ),
    ];
  }

  ngOnInit(): void {
    this.buildColumns(this.store.languageEntities());

    this.service.loadProjectClassificationsAdmin().subscribe((res) => {
      this.database = res?.data;
    });
  }

  onActiveToggle(row: any): void {
    const projectClassificationStatus = !row.isActive;
    this.masterdataService
      .activateProjectClassification(row, projectClassificationStatus)
      .subscribe({
        next: (updated) => {
          row.isActive = updated?.isActive ?? projectClassificationStatus;
        },
        error: (err) => {
          console.error(`Error updating ${this.databaseSelector} status`, err);
        },
      });
  }

  addElement() {
    this.router.navigate([
      '/admin',
      'database',
      this.databaseSelector,
      'addNew',
    ]);
  }
}
